import { useCallback, useEffect, useRef, useState } from "react";

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const TAP_MOVE_THRESHOLD_PX = 10;
const WHEEL_ZOOM_INTENSITY = 0.01;

function getTouchDistance(touches) {
  const dx = touches[0].clientX - touches[1].clientX;
  const dy = touches[0].clientY - touches[1].clientY;
  return Math.hypot(dx, dy);
}

function getTouchCenter(touches) {
  return {
    x: (touches[0].clientX + touches[1].clientX) / 2,
    y: (touches[0].clientY + touches[1].clientY) / 2,
  };
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

/**
 * Pinch-to-zoom and pan for a video viewport (touch + laptop trackpad / mouse).
 * Works while the video is playing.
 *
 * Trackpad: pinch → zoom toward cursor; two-finger scroll → pan when zoomed.
 * Mouse: drag when zoomed → pan.
 *
 * @param {React.RefObject<HTMLElement>} containerRef
 * @param {boolean} enabled
 * @param {{
 *   onGestureActivity?: () => void,
 *   onTap?: () => void,
 *   twoFingerGesturesOnly?: boolean,
 * }} [options]
 */
export function useVideoPinchZoom(containerRef, enabled = true, options = {}) {
  const { onGestureActivity, onTap, twoFingerGesturesOnly = false } = options;
  const [transform, setTransform] = useState({ scale: 1, x: 0, y: 0 });
  const transformRef = useRef(transform);
  const gestureRef = useRef(null);
  const activityRef = useRef(false);
  const callbacksRef = useRef({ onGestureActivity, onTap });
  const twoFingerOnlyRef = useRef(twoFingerGesturesOnly);
  twoFingerOnlyRef.current = twoFingerGesturesOnly;

  useEffect(() => {
    callbacksRef.current = { onGestureActivity, onTap };
  }, [onGestureActivity, onTap]);

  useEffect(() => {
    transformRef.current = transform;
  }, [transform]);

  const clampPan = useCallback((scale, x, y) => {
    const el = containerRef.current;
    if (!el || scale <= 1) return { scale: 1, x: 0, y: 0 };

    const rect = el.getBoundingClientRect();
    const maxX = ((scale - 1) * rect.width) / 2;
    const maxY = ((scale - 1) * rect.height) / 2;

    return {
      scale,
      x: clamp(x, -maxX, maxX),
      y: clamp(y, -maxY, maxY),
    };
  }, [containerRef]);

  const reset = useCallback(() => {
    const next = { scale: 1, x: 0, y: 0 };
    transformRef.current = next;
    setTransform(next);
    gestureRef.current = null;
    activityRef.current = false;
  }, []);

  const apply = useCallback(
    (scale = 1, x = 0, y = 0) => {
      const next = clampPan(
        Math.max(MIN_SCALE, Math.min(MAX_SCALE, Number(scale) || 1)),
        Number(x) || 0,
        Number(y) || 0
      );
      transformRef.current = next;
      setTransform(next);
      gestureRef.current = null;
      activityRef.current = false;
    },
    [clampPan]
  );

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !enabled) return undefined;

    const markActivity = () => {
      if (!activityRef.current) {
        activityRef.current = true;
        callbacksRef.current.onGestureActivity?.();
      }
    };

    const onTouchStart = (event) => {
      const current = transformRef.current;

      if (event.touches.length === 1) {
        gestureRef.current = {
          mode: !twoFingerOnlyRef.current && current.scale > 1 ? "pan" : "tap",
          startX: event.touches[0].clientX,
          startY: event.touches[0].clientY,
          originX: current.x,
          originY: current.y,
          moved: false,
          hadActivity: false,
        };
        return;
      }

      if (event.touches.length === 2) {
        event.preventDefault();
        markActivity();
        const center = getTouchCenter(event.touches);
        gestureRef.current = {
          mode: "pinch",
          startDistance: getTouchDistance(event.touches),
          startScale: current.scale,
          startX: current.x,
          startY: current.y,
          startCenterX: center.x,
          startCenterY: center.y,
          moved: true,
          hadActivity: true,
        };
      }
    };

    const onTouchMove = (event) => {
      const gesture = gestureRef.current;
      if (!gesture) return;

      if (gesture.mode === "tap" && event.touches.length === 1) {
        const dx = event.touches[0].clientX - gesture.startX;
        const dy = event.touches[0].clientY - gesture.startY;
        if (Math.hypot(dx, dy) > TAP_MOVE_THRESHOLD_PX) {
          gesture.moved = true;
        }
        return;
      }

      if (gesture.mode === "pan" && event.touches.length === 1) {
        if (twoFingerOnlyRef.current) {
          gestureRef.current = null;
          return;
        }
        event.preventDefault();
        const dx = event.touches[0].clientX - gesture.startX;
        const dy = event.touches[0].clientY - gesture.startY;
        if (Math.hypot(dx, dy) > TAP_MOVE_THRESHOLD_PX) {
          gesture.moved = true;
          gesture.hadActivity = true;
          markActivity();
        }
        const next = clampPan(
          transformRef.current.scale,
          gesture.originX + dx,
          gesture.originY + dy
        );
        transformRef.current = next;
        setTransform(next);
        return;
      }

      if (gesture.mode === "pinch" && event.touches.length === 2) {
        event.preventDefault();
        gesture.moved = true;
        gesture.hadActivity = true;
        markActivity();
        const distance = getTouchDistance(event.touches);
        const center = getTouchCenter(event.touches);
        const nextScale = clamp(
          gesture.startScale * (distance / gesture.startDistance),
          MIN_SCALE,
          MAX_SCALE
        );

        const panDx = center.x - gesture.startCenterX;
        const panDy = center.y - gesture.startCenterY;

        const next = clampPan(
          nextScale,
          gesture.startX + panDx,
          gesture.startY + panDy
        );
        transformRef.current = next;
        setTransform(next);
      }
    };

    const onTouchEnd = (event) => {
      const gesture = gestureRef.current;

      if (event.touches.length === 1 && gesture?.mode === "pinch") {
        if (twoFingerOnlyRef.current) {
          gestureRef.current = null;
          return;
        }
        const current = transformRef.current;
        gestureRef.current = {
          mode: "pan",
          startX: event.touches[0].clientX,
          startY: event.touches[0].clientY,
          originX: current.x,
          originY: current.y,
          moved: true,
          hadActivity: true,
        };
        return;
      }

      if (event.touches.length === 0) {
        const wasZoomPan = activityRef.current || gesture?.hadActivity || gesture?.moved;
        const wasTap =
          gesture &&
          !gesture.moved &&
          !gesture.hadActivity &&
          (gesture.mode === "tap" || gesture.mode === "pan");
        gestureRef.current = null;
        activityRef.current = false;
        // Only a clean tap shows UI. Zoom/pan release stays chrome-hidden.
        if (wasTap && !wasZoomPan) {
          callbacksRef.current.onTap?.();
        }
      }
    };

    // Trackpad pinch arrives as wheel + ctrlKey (Chrome/Safari/Firefox on macOS).
    // Two-finger scroll pans when already zoomed.
    const onWheel = (event) => {
      const current = transformRef.current;
      const isPinchZoom = event.ctrlKey || event.metaKey;

      if (!isPinchZoom && current.scale <= 1) return;

      event.preventDefault();
      markActivity();

      if (isPinchZoom) {
        const rect = el.getBoundingClientRect();
        const cx = event.clientX - rect.left - rect.width / 2;
        const cy = event.clientY - rect.top - rect.height / 2;
        const nextScale = clamp(
          current.scale * Math.exp(-event.deltaY * WHEEL_ZOOM_INTENSITY),
          MIN_SCALE,
          MAX_SCALE
        );
        if (nextScale <= MIN_SCALE) {
          const reset = { scale: 1, x: 0, y: 0 };
          transformRef.current = reset;
          setTransform(reset);
          return;
        }
        const ratio = nextScale / current.scale;
        const next = clampPan(
          nextScale,
          cx - (cx - current.x) * ratio,
          cy - (cy - current.y) * ratio
        );
        transformRef.current = next;
        setTransform(next);
        return;
      }

      const next = clampPan(
        current.scale,
        current.x - event.deltaX,
        current.y - event.deltaY
      );
      transformRef.current = next;
      setTransform(next);
    };

    const onPointerDown = (event) => {
      if (twoFingerOnlyRef.current) return;
      if (event.pointerType === "touch") return;
      if (event.button !== 0) return;
      const current = transformRef.current;
      if (current.scale <= 1) return;

      gestureRef.current = {
        mode: "pointer-pan",
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: current.x,
        originY: current.y,
        moved: false,
        hadActivity: false,
      };
      try {
        el.setPointerCapture(event.pointerId);
      } catch {
        /* ignore */
      }
    };

    const onPointerMove = (event) => {
      const gesture = gestureRef.current;
      if (!gesture || gesture.mode !== "pointer-pan") return;
      if (twoFingerOnlyRef.current) {
        gestureRef.current = null;
        return;
      }
      if (gesture.pointerId !== event.pointerId) return;

      const dx = event.clientX - gesture.startX;
      const dy = event.clientY - gesture.startY;
      if (Math.hypot(dx, dy) > TAP_MOVE_THRESHOLD_PX) {
        gesture.moved = true;
        gesture.hadActivity = true;
        markActivity();
      }
      const next = clampPan(
        transformRef.current.scale,
        gesture.originX + dx,
        gesture.originY + dy
      );
      transformRef.current = next;
      setTransform(next);
    };

    const onPointerUp = (event) => {
      const gesture = gestureRef.current;
      if (!gesture || gesture.mode !== "pointer-pan") return;
      if (gesture.pointerId !== event.pointerId) return;

      const wasZoomPan = activityRef.current || gesture.hadActivity || gesture.moved;
      gestureRef.current = null;
      activityRef.current = false;
      if (!wasZoomPan) {
        callbacksRef.current.onTap?.();
      }
    };

    el.addEventListener("touchstart", onTouchStart, { passive: false });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd);
    el.addEventListener("touchcancel", onTouchEnd);
    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerup", onPointerUp);
    el.addEventListener("pointercancel", onPointerUp);

    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", onPointerUp);
      el.removeEventListener("pointercancel", onPointerUp);
    };
  }, [clampPan, containerRef, enabled]);

  const style = {
    transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
  };

  return {
    style,
    scale: transform.scale,
    panX: transform.x,
    panY: transform.y,
    reset,
    apply,
  };
}

export default useVideoPinchZoom;
